<script setup lang="ts">
import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { StackedBarChartSpec } from "../sbi/protocol";
import { baseOption } from "./echarts-base";
import { autoYDomainFor } from "./chart-yscale.ts";
import {
  categoryLabelPolicy,
  plotWidthEstimate,
  verticalCategoryLabel,
} from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

const props = withDefaults(
  defineProps<{
    spec: StackedBarChartSpec;
    /** Field → format registry; first y field is the fallback lookup key. */
    formats?: Record<string, string>;
  }>(),
  {
    formats: () => ({}),
  },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

// `spec.data` is a `ChartData` union (rows-array | SqlMarker |
// DeriveMarker). The InsightChart dispatcher only mounts this
// subcomponent after the marker has resolved — narrow with
// `Array.isArray` so the rest of the component can index/map without
// repeated unions, and so a defective dispatch path can't crash render.
const rows = computed(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

const option = computed(() => {
  const base = baseOption({
    title: props.spec.title,
    // Per-axis `yFormat` → per-call `format` (DefaultOpts) → registry
    // format for the first y series. Heterogeneous series with
    // distinct formats should still set `yFormat` explicitly.
    yFormat:
      props.spec.yFormat ??
      props.spec.format ??
      props.formats[props.spec.y[0]],
    legendNames: props.spec.y,
  }, ctx.value);
  const categoryAxis = {
    ...base.xAxis,
    type: "category",
    data: rows.value.map((row) => row[props.spec.x]),
  };
  // Auto-scale operates on the per-row stack sum — that's the value the
  // top of each visible bar actually reaches, so it's the meaningful
  // domain. Non-finite cells contribute 0 to the sum, matching how
  // ECharts treats missing stack contributions.
  const stackSums = rows.value.map((row) => {
    let sum = 0;
    for (const field of props.spec.y) {
      const n = Number(row[field]);
      if (Number.isFinite(n)) sum += n;
    }
    return sum;
  });
  const autoDomain = autoYDomainFor(props.spec.yScale, stackSums);
  const valueAxis = {
    ...base.yAxis,
    type: "value" as const,
    ...(autoDomain ?? {}),
  };

  // Horizontal mode puts category labels on the y-axis. `containLabel`
  // (base grid) sizes the gutter to them; the width cap — 30% of the
  // chart, 48–160px — stops one long name squeezing the bars to
  // nothing, and truncates past it (full name in the tooltip).
  // Vertical mode rotates and truncates labels that don't fit their
  // slot instead of letting ECharts silently drop them.
  const xLabels = categoryAxis.data;
  const horizontalCategoryAxis = {
    ...categoryAxis,
    axisLabel: {
      ...(categoryAxis as { axisLabel?: object }).axisLabel,
      ...verticalCategoryLabel(xLabels, ctx.value.width),
    },
  };
  const verticalCategoryAxis = {
    ...categoryAxis,
    axisLabel: {
      ...(categoryAxis as { axisLabel?: object }).axisLabel,
      ...categoryLabelPolicy({
        labels: xLabels,
        plotWidth: plotWidthEstimate(ctx.value.width),
        mode: "rotate",
      }),
    },
  };

  return {
    ...base,
    xAxis: props.spec.horizontal ? valueAxis : verticalCategoryAxis,
    yAxis: props.spec.horizontal ? horizontalCategoryAxis : valueAxis,
    series: props.spec.y.map((field) => ({
      name: field,
      type: "bar",
      stack: "total",
      data: rows.value.map((row) => row[field]),
    })),
  };
});

const height = computed(() =>
  resolveChartHeight("stacked_bar", props.spec.height),
);

// `dataIndex` indexes `rows.value` (each series is built from the
// same row array); `seriesName` names the y-field that was clicked.
// We tag the datum with `_series` so the author can disambiguate
// which stack segment fired the click — the clicked row alone can't
// say which y field it was.
function onClick(p: { dataIndex: number }) {
  if (!props.spec.id || !p || typeof p.dataIndex !== "number") return;
  const params = p as { dataIndex: number; seriesName?: string };
  const row = rows.value[params.dataIndex];
  if (!row) return;
  emit("click-target", props.spec.id, {
    ...row,
    ...(params.seriesName !== undefined ? { _series: params.seriesName } : {}),
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
